'use client';

import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Timeframe, SportFilter, BetTypeFilter } from './ReportingFilters';

interface BetTypeStat {
  type: string;
  wins: number;
  losses: number;
  pushes: number;
  units: number;
  winPct: number;
  roi: number;
  picks: number;
}

interface BetTypeBreakdownProps {
  timeframe: Timeframe;
  sport: SportFilter;
  betType: BetTypeFilter;
}

const BET_TYPE_LABELS: Record<string, string> = {
  spread: 'Spread',
  moneyline: 'Moneyline',
  total: 'Total',
  prop: 'Prop',
  parlay: 'Parlay',
};

function getDateFilter(timeframe: Timeframe): string | null {
  const now = new Date();
  if (timeframe === '7d') { now.setDate(now.getDate() - 7); return now.toISOString(); }
  if (timeframe === '30d') { now.setDate(now.getDate() - 30); return now.toISOString(); }
  if (timeframe === '90d') { now.setDate(now.getDate() - 90); return now.toISOString(); }
  if (timeframe === 'ytd') { return new Date(now.getFullYear(), 0, 1).toISOString(); }
  return null;
}

export default function BetTypeBreakdown({ timeframe, sport, betType }: BetTypeBreakdownProps) {
  const [betTypeStats, setBetTypeStats] = useState<BetTypeStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        let query = supabase
          .from('picks')
          .select('bet_type, result, units, units_result, sport, posted_at')
          .neq('result', 'pending');

        if (sport !== 'all') query = query.eq('sport', sport);
        if (betType !== 'all') query = query.eq('bet_type', betType);
        const dateFrom = getDateFilter(timeframe);
        if (dateFrom) query = query.gte('posted_at', dateFrom);

        const { data, error } = await query;
        if (error) throw error;

        const byType: Record<string, { wins: number; losses: number; pushes: number; units: number; risked: number }> = {};
        (data || []).forEach((p) => {
          if (!byType[p.bet_type]) byType[p.bet_type] = { wins: 0, losses: 0, pushes: 0, units: 0, risked: 0 };
          byType[p.bet_type].units += p.units_result || 0;
          byType[p.bet_type].risked += p.units || 0;
          if (p.result === 'win') byType[p.bet_type].wins++;
          else if (p.result === 'loss') byType[p.bet_type].losses++;
          else if (p.result === 'push') byType[p.bet_type].pushes++;
        });

        const stats: BetTypeStat[] = Object.entries(byType).map(([type, d]) => {
          const winPct = d.wins + d.losses > 0 ? parseFloat(((d.wins / (d.wins + d.losses)) * 100).toFixed(1)) : 0;
          const roi = d.risked > 0 ? parseFloat(((d.units / d.risked) * 100).toFixed(1)) : 0;
          return {
            type: BET_TYPE_LABELS[type] || type,
            wins: d.wins,
            losses: d.losses,
            pushes: d.pushes,
            units: parseFloat(d.units.toFixed(1)),
            winPct,
            roi,
            picks: d.wins + d.losses + d.pushes,
          };
        });

        setBetTypeStats(stats.sort((a, b) => b.picks - a.picks));
      } catch {
        setBetTypeStats([]);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [timeframe, sport, betType]);

  return (
    <div className="stat-card mb-8">
      <h2 className="text-lg font-bold text-[hsl(var(--foreground))] mb-5">Performance by Bet Type</h2>

      {loading ? (
        <div className="flex items-center justify-center py-8 gap-2">
          <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading stats...</span>
        </div>
      ) : betTypeStats.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-[hsl(var(--foreground-muted))]">No graded picks yet. Stats will appear once picks are graded.</p>
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                {['Bet Type', 'Record', 'Win %', 'Net Units', 'ROI', 'Total Picks'].map((h) => (
                  <th key={h} className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))] pr-6">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {betTypeStats.map((b, i) => (
                <tr key={b.type} className={`border-b border-[hsl(var(--border)/0.5)] hover:bg-[hsl(var(--card-elevated))] transition-colors duration-100 ${i % 2 === 0 ? '' : 'bg-[hsl(var(--card-elevated)/0.3)]'}`}>
                  <td className="py-3.5 pr-6"><span className="font-medium text-sm text-[hsl(var(--foreground))]">{b.type}</span></td>
                  <td className="py-3.5 pr-6">
                    <span className="font-mono text-sm tabular-nums text-[hsl(var(--foreground))]">
                      {b.wins}-{b.losses}{b.pushes > 0 ? `-${b.pushes}` : ''}
                    </span>
                  </td>
                  <td className="py-3.5 pr-6">
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-1.5 rounded-full bg-[hsl(var(--border))]">
                        <div className="h-full rounded-full bg-[hsl(var(--primary))]" style={{ width: `${Math.min(b.winPct, 100)}%` }} />
                      </div>
                      <span className="font-mono text-sm tabular-nums text-[hsl(var(--foreground))]">{b.winPct.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="py-3.5 pr-6">
                    <div className="flex items-center gap-1.5">
                      {b.units >= 0 ? <TrendingUp size={13} className="text-[hsl(var(--primary))]" /> : <TrendingDown size={13} className="text-red-400" />}
                      <span className={`font-mono font-bold text-sm tabular-nums ${b.units >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
                        {b.units >= 0 ? '+' : ''}{b.units.toFixed(1)}u
                      </span>
                    </div>
                  </td>
                  <td className="py-3.5 pr-6">
                    <span className={`font-mono text-sm tabular-nums font-medium ${b.roi >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
                      {b.roi >= 0 ? '+' : ''}{b.roi.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3.5">
                    <span className="font-mono text-sm tabular-nums text-[hsl(var(--foreground-muted))]">{b.picks}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}